import React, { useState, useEffect } from "react";

import "./AnalyticsDashboard.css";
import DauChart from "./DauChart";
import WebsiteTrafficTrendChart from "./WebsiteTrafficTrendChart";
import DeviceTrafficChart from "./DeviceTrafficChart";
import UserGrowthChart from "./UserGrowthChart";
import UserRetentionChart from "./UserRetentionChart";
import { useNavigate } from "react-router-dom";
import PopularPagesList from "./PopularPagesList";
import {
  RoulettePlayCountChart,
  RouletteUniquePlayersChart,
} from "./RouletteTrendsChart";
import TrafficOverviewWidget from "./TrafficOverviewWidget";
import TrafficByLanguageList from "./TrafficByLanguageList";
import ChatSafetyOverview from "./ChatSafetyOverview";
import AlertTrendChart from "./AlertTrendChart";
import CategoryBreakdownChart from "./CategoryBreakdownChart";
import SeverityDistributionChart from "./SeverityDistributionChart";
import RepeatOffendersList from "./RepeatOffendersList";
import PeakActivityHeatmap from "./PeakActivityHeatmap";

import MetricModal from "./MetricModal";

import userAppIcon from "../../assets/lobo.png";
import { FiGlobe, FiHome, FiMessageSquare } from 'react-icons/fi';
import { FcCollaboration, FcBullish } from "react-icons/fc";
import { GiArtificialIntelligence } from "react-icons/gi";
import { RiChatPollFill } from "react-icons/ri";
import { AiFillSafetyCertificate } from "react-icons/ai";
import { FaUserFriends } from "react-icons/fa";
import { FaUsers } from "react-icons/fa6";
import { IoChatbubblesSharp } from "react-icons/io5";
import MessageTrendChart from "./MessageTrendChart";

// Multiple API base URLs for different services
const API_BASE_URLS = {
  analytics: "https://alb.api-prod-conquerlifeco.com/api/analytics",
  chatAnalytics: "https://alb.api-prod-conquerlifeco.com/api/chat/analytics",
  // Add more base URLs as needed
};

const fetchAnalyticsData = async (category, metric, interval, apiBaseUrl, platform) => {
  try {
    // Build URL based on whether platform is provided
    const url = platform 
      ? `${apiBaseUrl}/${category}/${metric}/${platform}/${interval}`
      : `${apiBaseUrl}/${category}/${metric}/${interval}`;

      const response = await fetch(url);

    // const response = await fetch(
    //   `${apiBaseUrl}/${category}/${metric}/${interval}`
    // );
    if (!response.ok) throw new Error("Failed to fetch data");
    return await response.json();
  } catch (error) {
    console.error("Error fetching analytics:", error);
    return null;
  }
};

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("user_app_website");
  const [analyticsData, setAnalyticsData] = useState({});
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState("ytd");
  // const [deviceType, setDeviceType] = useState("all"); 
  const [customDays, setCustomDays] = useState("");
  const [customMonth, setCustomMonth] = useState("");
  const [customYear, setCustomYear] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [showYearInput, setShowYearInput] = useState(false);
  const [showMonthInput, setShowMonthInput] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({
    user_app: false,
  conquer_website: false,
  });
  const [expandedAISections, setExpandedAISections] = useState({
    user_service: false,
    chat_service: false,
  });
const [expandedUserSections, setExpandedUserSections] = useState({
  user_trends: false,
  user_interactions: false,
});
const [expandedChatSections, setExpandedChatSections] = useState({
  chat_trends: false,
  chat_safety: false,
});
  const [expandedMetric, setExpandedMetric] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState("ALL_PLATFORMS");
  const [isOpen, setIsOpen] = useState(false);

  const sectionConfig = {
    user_service: {
      label: "User Service",
      icon: FaUsers,
    },
    chat_service: {
      label: "Chat Service",
      icon: FiMessageSquare,
    },
    user_trends: {
      label: "User Trends",
      icon: FcBullish,
    },
    user_interactions: {
      label: "User Interactions",
      icon: FcCollaboration,
    },
    chat_trends: {
      label: "Chat Trends",
      icon: RiChatPollFill,
    },
    chat_safety: {
      label: "Chat Safety",
      icon: AiFillSafetyCertificate,
    },
  };


  useEffect(() => {
  if (!selectedCategory) {
    const firstParent = categories[0];
    if (firstParent?.children?.length > 0) {
      setSelectedCategory(firstParent.children[0].id);
    }
  }
}, []);

  // Hierarchical category configuration
const categories = [
  { 
    id: "user_app",
    name: "User App",
    icon: userAppIcon,
    color: "#667eea",
    type: "parent",
    children: [
      {
        id: "user_app_website",
        name: "www.conquerlife.io",
        icon: FiGlobe,
        color: "#667eea",
        apiBaseUrl: API_BASE_URLS.analytics,
        apiCategory: "user_website"
      },
      {
        id: "user_insights",
        name: "User Insights",
        icon: FaUsers,
        color: "#667eea",
        apiBaseUrl: null,
        apiCategory: null
      },
      {
        id: "chat_insights",
        name: "Chat Insights",
        icon: IoChatbubblesSharp,
        color: "#667eea",
        apiBaseUrl: null,
        apiCategory: null
      },
      {
        id: "ai_usage",
        name: "AI Usage",
        icon: GiArtificialIntelligence,
        color: "#FBBF24",
        apiBaseUrl: null, // handled per section
        apiCategory: null
      }

    ]
  },
  { 
    id: "conquer_website",
    name: "Company Website",
    icon: "https://conquerlifepublicbucket.s3.us-east-1.amazonaws.com/conquerLifeLogo.png",
    color: "#16A085",
    type: "parent",
    children: [
      {
        id: "conquer_landing_page",
        name: "www.conquerlifeco.com",
        icon: FiHome,
        color: "#FBBF24",
        apiBaseUrl: API_BASE_URLS.analytics,
        apiCategory: "conquer_landing_page"
      }
    ]
  }
];

  // Helper function to check if icon is an image URL
  const isImageUrl = (icon) => {
  // Check if it's a string URL (http/https)
  if (typeof icon === 'string' && (icon.startsWith('http://') || icon.startsWith('https://'))) {
    return true;
  }
  // Check if it's an imported image (local file path)
  if (typeof icon === 'string' && (icon.startsWith('/') || icon.startsWith('./') || icon.includes('.png') || icon.includes('.jpg') || icon.includes('.svg'))) {
    return true;
  }
  return false;
};

// New helper function to check if icon is a React component
const isReactIconComponent = (icon) => {
  return typeof icon === 'function';
};

  const getDateRangeLabel = () => {
    if (dateRange.startsWith("custom-")) {
      const days = dateRange.split("-")[1];
      return `Last ${days} Day${days === "1" ? "" : "s"}`;
    }
    if (dateRange.startsWith("month-")) {
      const months = dateRange.split("-")[1];
      return `Last ${months} Month${months === "1" ? "" : "s"}`;
    }
    if (dateRange.startsWith("year-")) {
      const year = dateRange.split("-")[1];
      return `Year ${year}`;
    }

    const rangeLabels = {
      "30min": "Last 30 Minutes",
      "24h": "Last 24 Hours",
      7: "Last 7 Days",
      10: "Last 10 Days",
      30: "Last 30 Days",
      90: "Last 90 Days",
      "1_month": "Last 1 Month",
      "3_months": "Last 3 Months",
      "6_months": "Last 6 Months",
      "8_months": "Last 8 Months",
      past_year: "Past Year",
      ytd: "Year to Date",
    };

    return rangeLabels[dateRange] || "Select Range";
  };

  // Get current category config
  const getCurrentCategoryConfig = () => {
    // Check if it's a standalone category
    const standalone = categories.find(cat => cat.id === selectedCategory && cat.type === "standalone");
    if (standalone) return standalone;

    // Check if it's a child category
    for (const parent of categories) {
      if (parent.type === "parent" && parent.children) {
        const child = parent.children.find(c => c.id === selectedCategory);
        if (child) return child;
      }
    }
    return null;
  };

  const toggleSection = (group, section) => {
    if (group === "ai") {
      setExpandedAISections(prev => ({
        ...prev,
        [section]: !prev[section],
      }));
    }

    if (group === "user") {
      setExpandedUserSections(prev => ({
        ...prev,
        [section]: !prev[section],
      }));
    }

    if (group === "chat") {
      setExpandedChatSections(prev => ({
        ...prev,
        [section]: !prev[section],
      }));
    }
  };

  // Metrics configuration per category
  const metricsConfig = {
    user_insights: {
      user_trends: [
        { id: "total_users", title: "Total Users", icon: "👤", type: "number" },
        {
          id: "active_users",
          title: `Active Users (${getDateRangeLabel()})`,
          icon: "✅",
          type: "number",
        },
        {
          id: "new_users",
          title: `New Users (${getDateRangeLabel()})`,
          icon: "🆕",
          type: "number",
        },
        {
          id: "user_growth",
          title: "User Growth Trend",
          icon: "📈",
          type: "user_growth_chart",
        },
        {
          id: "daily_active_users",
          title: "Daily Active Users",
          icon: "📊",
          type: "dau_chart",
          graphName: "Daily Active Users",
          dataTitle: "Active Users",
        },
        {
          id: "user_retention",
          title: "User Retention Rate",
          icon: "🔄",
          type: "user_retention_chart",
        },
        {
          id: "users_for_recommendation",
          title: `Users Available for Recommendation (${getDateRangeLabel()})`,
          icon: "🆕",
          type: "number",
        },
        {
          id: "bapp-user-accounts",
          title: `Business App User Accounts (${getDateRangeLabel()})`,
          icon: "🆕",
          type: "number",
        },
        {
          id: "deleted_user_accounts",
          title: `Deleted User Accounts (${getDateRangeLabel()})`,
          icon: "🆕",
          type: "number",
        },
      ],
      pages: [
        {
          id: "top_8_popular_pages",
          title: "Popular Pages",
          icon: "🗒️",
          type: "list",
        },
        {
          id: "roulette_trends",
          title: "Roulette Plays Trend",
          icon: "🎰",
          type: "roulette_play_count_chart",
        }
        // ,
        // {
        //   id: "roulette_unique_players",
        //   title: "Unique Roulette Players",
        //   icon: "👥",
        //   type: "roulette_unique_players_chart",
        // },
      ]
    },
    ai_usage: {
      user_service: [
        {
          id: "user_total_profanity_checks",
          title: "Total Profanity Checks",
          icon: "🗒️",
          type: "number",
        },
        {
          id: "user_trends_total_profanity_checks",
          title: "Profanity Checks Trend",
          icon: "📈",
          type: "dau_chart",
          graphName: "Total Profanity Checks Trend",
          dataTitle: "Count",
        },
        {
          id: "user_update_username_profanity_checks",
          title: "Update Username Profanity Checks Count",
          icon: "🗒️",
          type: "number",
        },
        {
          id: "user_trends_update_username_profanity_checks",
          title: "Update Username Profanity Checks Trend",
          icon: "📈",
          type: "dau_chart",
          graphName: "Update Username Profanity Checks Trend",
          dataTitle: "Count",
        },
        {
          id: "user_update_about_user_profanity_checks",
          title: "Update About User Profanity Checks Count",
          icon: "🗒️",
          type: "number",
        },
        {
          id: "user_trends_update_about_user_profanity_checks",
          title: "Update About User Profanity Checks Trend",
          icon: "📈",
          type: "dau_chart",
          graphName: "Update About User Profanity Checks Trend",
          dataTitle: "Count",
        },
        {
          id: "user_username_validation_profanity_checks",
          title: "Username Validation Profanity Checks Count",
          icon: "🗒️",
          type: "number",
        },
        {
          id: "user_trends_username_validation_profanity_checks",
          title: "Username Validation Profanity Checks Trend",
          icon: "📈",
          type: "dau_chart",
          graphName: "Username Validation Profanity Checks Trend",
          dataTitle: "Count",
        },
      ],
      chat_service: [
        {
          id: "total_profanity_checks",
          title: "Total Profanity Checks",
          icon: "🗒️",
          type: "number",
        },
        {
          id: "trends_total_profanity_checks",
          title: "Profanity Checks Trend",
          icon: "📈",
          type: "dau_chart",
          graphName: "Total Profanity Checks Trend",
          dataTitle: "Count",
        },
        {
          id: "total_group_name_profanity_checks",
          title: "Total Group Name Profanity Checks",
          icon: "🗒️",
          type: "number",
        },
        {
          id: "group_name_profanity_checks_trends",
          title: "Group Name Profanity Checks Trend",
          icon: "📈",
          type: "dau_chart",
          graphName: "Group Name Profanity Checks Trend",
          dataTitle: "Count",
        },
        {
          id: "total_group_description_profanity_checks",
          title: "Total Group Description Profanity Checks",
          icon: "🗒️",
          type: "number",
        },
        {
          id: "group_description_profanity_checks_trends",
          title: "Group Description Profanity Checks Trend",
          icon: "📈",
          type: "dau_chart",
          graphName: "Group Description Profanity Checks Trend",
          dataTitle: "Count",
        },
        {
          id: "total_update_group_name_profanity_checks",
          title: "Total Update Group Name Profanity Checks",
          icon: "🗒️",
          type: "number",
        },
        {
          id: "update_group_name_profanity_checks_trends",
          title: "Update Group Name Profanity Checks Trends",
          icon: "📈",
          type: "dau_chart",
          graphName: "Update Group Name Profanity Checks Trends",
          dataTitle: "Count",
        },
        {
          id: "total_update_group_description_profanity_checks",
          title: "Total Update Group Description Profanity Checks",
          icon: "🗒️",
          type: "number",
        },
        {
          id: "update_group_description_profanity_checks_trends",
          title: "Update Group Description Profanity Checks Trends",
          icon: "📈",
          type: "dau_chart",
          graphName: "Update Group Description Profanity Checks Trends",
          dataTitle: "Count",
        },
        {
          id: "total_update_event_username_profanity_checks",
          title: "Total Update Event Username Profanity Checks",
          icon: "🗒️",
          type: "number",
        },
        {
          id: "update_event_username_profanity_checks_trends",
          title: "Update Event Username Profanity Checks Trends",
          icon: "📈",
          type: "dau_chart",
          graphName: "Update Event Username Profanity Checks Trends",
          dataTitle: "Count",
        },
        {
          id: "total_update_event_name_profanity_checks",
          title: "Total Update Event Name Profanity Checks",
          icon: "🗒️",
          type: "number",
        },
        {
          id: "update_event_name_profanity_checks_trends",
          title: "Update Event Name Profanity Checks Trends",
          icon: "📈",
          type: "dau_chart",
          graphName: "Update Event Name Profanity Checks Trends",
          dataTitle: "Count",
        },
        {
          id: "total_update_event_description_profanity_checks",
          title: "Total Update Event Description Profanity Checks",
          icon: "🗒️",
          type: "number",
        },
        {
          id: "update_event_description_profanity_checks_trends",
          title: "Update Event Description Profanity Checks Trends",
          icon: "📈",
          type: "dau_chart",
          graphName: "Update Event Description Profanity Checks Trends",
          dataTitle: "Count",
        },
        {
          id: "total_event_start_new_chat_profanity_checks",
          title: "Total Event Start New Chat Profanity Checks",
          icon: "🗒️",
          type: "number",
        },
        {
          id: "event_start_new_chat_profanity_checks_trends",
          title: "Event Start New Chat Profanity Checks Trends",
          icon: "📈",
          type: "dau_chart",
          graphName: "Event Start New Chat Profanity Checks Trends",
          dataTitle: "Count",
        },
      ]
    },
    chat_insights: {
      chat_trends: [
        { id: "active_chats", title: "Active Chats", icon: "🔥", type: "number" },
        { id: "group_chats", title: "Group Chats", icon: "👥", type: "number" },
        {
          id: "private_chats",
          title: "Private Chats",
          icon: "🔐",
          type: "number",
        },
        {
          id: "message_trend",
          title: "Message Trend",
          icon: "📈",
          type: "message_trend_chart",
        },
      ],
      chat_safety: [
        {
          id: "overview",
          title: "Safety Overview",
          icon: "🛡️",
          type: "chat_safety_overview_widget",
          graphName: "Safety Overview",
        },
        {
          id: "chat_alert_trends",
          title: "Chat Alert Trends",
          icon: "📈",
          type: "alert_trend_chart",
          graphName: "Alert Trend",
        },
        {
          id: "category_breakdown",
          title: "Category Breakdown",
          icon: "📊",
          type: "category_breakdown_chart",
          graphName: "Category Breakdown",
        },
        {
          id: "severity_distribution",
          title: "Severity Distribution",
          icon: "🎯",
          type: "severity_distribution_chart",
          graphName: "Severity Distribution",
        },
        {
          id: "repeat_offenders",
          title: "Top 10 Repeat Offenders",
          icon: "⚠️",
          type: "repeat_offenders_list",
          graphName: "Repeat Offenders",
        },
        {
          id: "peak_activity_hours",
          title: "Peak Activity Hours",
          icon: "🔥",
          type: "peak_activity_heatmap",
          graphName: "Peak Activity Hours",
        },
        // Add other chat safety widgets here later
      ],
    },
    user_app_website: [
      {
        id: "traffic_trends",
        title: "Traffic Trends",
        icon: "📊",
        type: "web_traffic_trend_chart",
        graphName: "Traffic Trends",
      },
      {
        id: "traffic_by_device",
        title: "Traffic by Device Type",
        icon: "📊",
        type: "traffic_by_device_chart",
        graphName: "Traffic by Device Type",
      },
      {
        id: "traffic_overview",
        title: "Traffic Overview",
        icon: "📊",
        type: "traffic_overview_widget",
        graphName: "Traffic Overview",
      },
      {
        id: "traffic_by_preferred_lang",
        title: "Traffic by Preferred Language",
        icon: "📊",
        type: "traffic_by_preferred_lang_list",
        graphName: "Traffic by Preferred Language",
      },

    ],
    conquer_landing_page:[],
  };

  // Load data when category or date range changes
  useEffect(() => {
    loadCategoryData();
  }, [selectedCategory, dateRange, customDays, customYear, selectedPlatform]);

  const loadCategoryData = async () => {
    setLoading(true);
    const metrics = metricsConfig[selectedCategory];
    const categoryConfig = getCurrentCategoryConfig();
    const data = {};

    if (selectedCategory === "ai_usage") {
      const aiMetrics = metricsConfig.ai_usage;

      for (const sectionKey of Object.keys(aiMetrics)) {
        const metrics = aiMetrics[sectionKey];

        const apiBaseUrl =
          sectionKey === "user_service"
            ? API_BASE_URLS.analytics
            : API_BASE_URLS.chatAnalytics;

        const apiCategory =
          sectionKey === "user_service"
            ? "ai_usage"
            : "chat_service_ai_usage";

        for (const metric of metrics) {
          const result = await fetchAnalyticsData(
            apiCategory,
            metric.id,
            getDateRangeLabel(),
            apiBaseUrl
          );

          data[metric.id] = result || { value: 0, data: [] };
        }
      }
      setAnalyticsData(data);
      setLoading(false);
      return;
    }
    if (selectedCategory === "user_insights") {
      const userMetrics = metricsConfig.user_insights;

      for (const sectionKey of Object.keys(userMetrics)) {
        const metrics = userMetrics[sectionKey];

        let apiCategory = "";
        let apiBaseUrl = API_BASE_URLS.analytics;

        if (sectionKey === "user_trends") {
          apiCategory = "users";
        } else if (sectionKey === "pages") {
          apiCategory = "pages";
        }

        for (const metric of metrics) {
          // Pass platform for daily_active_users AND user_growth
          const platform =
            metric.id === "daily_active_users" ||
            metric.id === "user_growth" ||
            metric.id === "top_8_popular_pages"
              ? selectedPlatform
              : null;

          const result = await fetchAnalyticsData(
            apiCategory,
            metric.id,
            getDateRangeLabel(),
            apiBaseUrl,
            platform
          );

          data[metric.id] = result || { value: 0, data: [] };
        }
      }

      setAnalyticsData(data);
      setLoading(false);
      return;
    }

    if (selectedCategory === "chat_insights") {
      const userMetrics = metricsConfig.chat_insights;

      for (const sectionKey of Object.keys(userMetrics)) {
        const metrics = userMetrics[sectionKey];

        let apiCategory = "";
        let apiBaseUrl = API_BASE_URLS.chatAnalytics;

        if (sectionKey === "chat_trends") {
          apiCategory = "chat_trends";
        } else if (sectionKey === "chat_safety") {
          apiCategory = "chat_safety";
        }

        for (const metric of metrics) {
          const result = await fetchAnalyticsData(
            apiCategory,
            metric.id,
            getDateRangeLabel(),
            apiBaseUrl
          );

          data[metric.id] = result || { value: 0, data: [] };
        }
      }

      setAnalyticsData(data);
      setLoading(false);
      return;
    }


    if (metrics && categoryConfig) {
      const apiBaseUrl = categoryConfig.apiBaseUrl || API_BASE_URLS.analytics;
      const apiCategory = categoryConfig.apiCategory || selectedCategory;

      for (const metric of metrics) {
         // Skip data fetching for widgets that handle their own API calls
        if (metrics.type === 'chat_safety_overview_widget' || 
            metrics.type === 'traffic_overview_widget') {
          continue;
        }
        const result = await fetchAnalyticsData(
          apiCategory,
          metric.id,
          getDateRangeLabel(),
          apiBaseUrl
        );
        data[metric.id] = result || { value: 0, data: [] };
      }
    }

    setAnalyticsData(data);
    setLoading(false);
  };

  const handleDateRangeChange = (e) => {
  const value = e.target.value;

  // Reset all custom states first
  setShowCustomInput(false);
  setShowMonthInput(false);
  setShowYearInput(false);

  if (value === "custom") {
    setShowCustomInput(true);
    return;
  }

  if (value === "month") {
    setShowMonthInput(true);
    return;
  }

  if (value === "year") {
    setShowYearInput(true);
    return;
  }

  // Otherwise it's a normal preset
  setDateRange(value);
};


  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleCategoryChange = (categoryId, isParent = false) => {
  if (isParent) {
    const isCurrentlyExpanded = expandedCategories[categoryId];

    toggleCategory(categoryId);

    // Only handle selection when expanding
    if (!isCurrentlyExpanded) {
      const parentCategory = categories.find(
        cat => cat.id === categoryId
      );

      if (parentCategory?.children?.length > 0) {
        const isChildAlreadySelected = parentCategory.children.some(
          child => child.id === selectedCategory
        );

        // ✅ Only select first child IF none is selected
        if (!isChildAlreadySelected) {
          setSelectedCategory(parentCategory.children[0].id);
        }
      }
    }

    // ❌ Do nothing on collapse
  } else {
    setSelectedCategory(categoryId);
    setIsMobileMenuOpen(false);
  }
};

  const handleCustomDaysSubmit = () => {
    if (customDays && parseInt(customDays) > 0) {
      setDateRange(`custom-${customDays}`);
      // setShowCustomInput(false);
      loadCategoryData();
    }
  };

  const handleCustomMonthSubmit = () => {
    console.log("Submitting:", `month-${customMonth}`);

    if (customMonth && parseInt(customMonth) > 0) {
      setDateRange(`month-${customMonth}`);
      // setShowCustomInput(false);
      loadCategoryData();
    }

  };

  const handleCustomYearSubmit = () => {
    if (
      customYear &&
      parseInt(customYear) >= 2020 &&
      parseInt(customYear) <= new Date().getFullYear()
    ) {
      setDateRange(`year-${customYear}`);
      setShowYearInput(false);
      loadCategoryData();
    }
  };

  const formatValue = (value, type) => {
    if (!value && value !== 0) return "N/A";

    switch (type) {
      case "number":
        return value.toLocaleString();
      case "percentage":
        return `${value}%`;
      case "time":
        return `${value}s`;
      default:
        return value;
    }
  };

  const getCurrentCategoryName = () => {
    const config = getCurrentCategoryConfig();
    return config ? config.name : "Analytics";
  };

  const getCurrentCategoryIcon = () => {
    const config = getCurrentCategoryConfig();
    return config ? config.icon : "📊";
  };

  const handleTileClick = (metric) => {
  setExpandedMetric(metric);
};

const closeModal = () => {
  setExpandedMetric(null);
};

const renderExpandedContent = (metric) => {
  const data = analyticsData[metric.id];

  if (metric.type === "dau_chart") {
    return (
      <div className="modal-chart-container">
        <DauChart
          data={data}
          interval={getDateRangeLabel()}
          graphName={metric.graphName || "Trends"}
          dataTitle={metric.dataTitle || "Count"}
        />
      </div>
    );
  } else if (metric.type === "user_growth_chart") {
    return (
      <div className="modal-chart-container">
        <UserGrowthChart data={data} interval={getDateRangeLabel()} />
      </div>
    );
  } else if (metric.type === "user_retention_chart") {
    return (
      <div className="modal-chart-container">
        <UserRetentionChart data={data} interval={getDateRangeLabel()} />
      </div>
    );
  } else if (metric.type === "list") {
    return (
      <div className="modal-list-container">
        <PopularPagesList 
        data={data} 
        interval={getDateRangeLabel()}
        platform={metric.id === "top_8_popular_pages" ? selectedPlatform : null}
      />
      </div>
    );
  } else if (metric.type === "roulette_play_count_chart") {
    return (
      <div className="modal-chart-container">
        <RoulettePlayCountChart data={data} interval={getDateRangeLabel()} />
      </div>
    );
  } else if (metric.type === "roulette_unique_players_chart") {
    return (
      <div className="modal-chart-container">
        <RouletteUniquePlayersChart data={data} interval={getDateRangeLabel()} />
      </div>
    );
  } else if (metric.type === "traffic_overview_widget") {
    return (
      <div className="modal-widget-container">
        <TrafficOverviewWidget
          interval={getDateRangeLabel()}
          apiBaseUrl={getCurrentCategoryConfig()?.apiBaseUrl || API_BASE_URLS.analytics}
        />
      </div>
    );
  } else if (metric.type === "web_traffic_trend_chart") {
    return (
      <div className="modal-chart-container">
        <WebsiteTrafficTrendChart
          data={data}
          interval={getDateRangeLabel()}
          graphName={metric.graphName || "Trends"}
          dataTitle={metric.dataTitle || "Count"}
        />
      </div>
    );
  } else if (metric.type === "traffic_by_device_chart") {
    return (
      <div className="modal-chart-container">
        <DeviceTrafficChart data={data} interval={getDateRangeLabel()} />
      </div>
    );
  } else if (metric.type === "traffic_by_preferred_lang_list") {
    return (
      <div className="modal-list-container">
        <TrafficByLanguageList data={data} interval={getDateRangeLabel()} />
      </div>
    );
  } else if (metric.type === "chat_safety_overview_widget") {
    return (
      <div className="modal-widget-container">
        <ChatSafetyOverview
          interval={getDateRangeLabel()}
          apiBaseUrl={getCurrentCategoryConfig()?.apiBaseUrl || API_BASE_URLS.chatAnalytics}
        />
      </div>
    );
  }else if (metric.type === "alert_trend_chart") {
    return (
      <div className="modal-chart-container">
        <AlertTrendChart
          interval={getDateRangeLabel()}
          apiBaseUrl={getCurrentCategoryConfig()?.apiBaseUrl || API_BASE_URLS.chatAnalytics}
        />
      </div>
    );
  } else if (metric.type === "category_breakdown_chart") {
    return (
      <div className="modal-chart-container">
        <CategoryBreakdownChart
          interval={getDateRangeLabel()}
          apiBaseUrl={getCurrentCategoryConfig()?.apiBaseUrl || API_BASE_URLS.chatAnalytics}
        />
      </div>
    );
  } else if (metric.type === "severity_distribution_chart") {
    return (
      <div className="modal-chart-container">
        <SeverityDistributionChart
          interval={getDateRangeLabel()}
          apiBaseUrl={getCurrentCategoryConfig()?.apiBaseUrl || API_BASE_URLS.chatAnalytics}
        />
      </div>
    );
  } else if (metric.type === "repeat_offenders_list") {
    return (
      <div className="modal-list-container">
        <RepeatOffendersList
          interval={getDateRangeLabel()}
          apiBaseUrl={getCurrentCategoryConfig()?.apiBaseUrl || API_BASE_URLS.chatAnalytics}
        />
      </div>
    );
  }
  else if (metric.type === "peak_activity_heatmap") {
    return (
      <div className="modal-list-container">
        <PeakActivityHeatmap
          interval={getDateRangeLabel()}
          apiBaseUrl={getCurrentCategoryConfig()?.apiBaseUrl || API_BASE_URLS.chatAnalytics}
        />
      </div>
    );
  } else if (metric.type === "message_trend_chart") {
    return (
      <div className="modal-chart-container">
        <MessageTrendChart
          interval={getDateRangeLabel()}
          apiBaseUrl={getCurrentCategoryConfig()?.apiBaseUrl || API_BASE_URLS.chatAnalytics}
        />
      </div>
    );
  }

  return null;
};

const renderMetricTile = (metric) => {
  const data = analyticsData[metric.id];
  const value = data > 0 ? data : 0;

  // Determine if this metric type can be expanded
  const isExpandable = [
    "dau_chart",
    "user_growth_chart",
    "user_retention_chart",
    "list",
    "roulette_play_count_chart",
    "roulette_unique_players_chart",
    "chart",
    "traffic_overview_widget",
    "web_traffic_trend_chart",
    "traffic_by_device_chart",
    "traffic_by_preferred_lang_list",
    "chat_safety_overview_widget",
    "alert_trend_chart",
    "category_breakdown_chart",
    "severity_distribution_chart",
    "repeat_offenders_list", 
    "peak_activity_heatmap", 
    "message_trend_chart",
  ].includes(metric.type);

  return (
    <div
      key={metric.id}
      className={`metric-tile ${
        metric.type === "list" ? "full-width-tile" :
        metric.type === "traffic_overview_stats" ? "full-width-tile" :
        metric.type === "traffic_overview_widget" ? "full-width-tile" :
        metric.type === "traffic_by_preferred_lang_list" ? "full-width-tile" :
        metric.type === "chat_safety_overview_widget" ? "full-width-tile chat-safety-tile" : 
        metric.type === "chat_safety_overview_widget" ? "full-width-tile chat-safety-tile" : 
        metric.type === "peak_activity_heatmap" ? "full-width-tile" :
        metric.type === "repeat_offenders_list" ? "full-width-tile chat-safety-tile" : ""
      } ${isExpandable ? "expandable-tile" : ""}`}
      onClick={(e) => {
        // Only expand if clicking on the tile itself, not on interactive content
        if (isExpandable && (e.target === e.currentTarget || e.target.classList.contains('expand-indicator') || e.target.closest('.expand-indicator'))) {
          handleTileClick(metric);
        }
      }}
      style={{ cursor: isExpandable ? "pointer" : "default" }}
    >
      {isExpandable && (
        <div className="expand-indicator">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </div>
      )}

      {/* Platform Selector - Show on specific widgets */}
      {(metric.id === "daily_active_users" || 
        metric.id === "user_growth" || 
        metric.id === "top_8_popular_pages") && (
        <div className="widget-platform-selector" onClick={(e) => e.stopPropagation()}>
          <select
            value={selectedPlatform}
            onChange={(e) => setSelectedPlatform(e.target.value)}
          >
            <option value="ALL_PLATFORMS">All Platforms</option>
            <option value="IOS">iOS</option>
            <option value="ANDROID">Android</option>
            <option value="WEB">Web</option>
          </select>
        </div>
      )}
      <div className="metric-header">
        <span className="metric-icon">{metric.icon}</span>
        <h3 className="metric-title">{metric.title}</h3>
      </div>

      <div className="metric-content">
        {loading ? (
          <div className="metric-loading">
            <div className="spinner"></div>
            <span>Loading...</span>
          </div>
        ) : metric.type === "dau_chart" ? (
          <div className="metric-chart">
            <DauChart
              data={data}
              interval={getDateRangeLabel()}
              graphName={metric.graphName || "Trends"}
              dataTitle={metric.dataTitle || "Count"}
            />
          </div>
        ) : metric.type === "user_growth_chart" ? (
          <div className="metric-chart">
            <UserGrowthChart data={data} interval={getDateRangeLabel()} />
          </div>
        ) : metric.type === "user_retention_chart" ? (
          <div className="metric-chart">
            <UserRetentionChart data={data} interval={getDateRangeLabel()} />
          </div>
        ) : metric.type === "list" ? (
          <div className="metric-list">
            <PopularPagesList 
              data={data} 
              interval={getDateRangeLabel()}
              platform={metric.id === "top_8_popular_pages" ? selectedPlatform : null}
            />
          </div>
        ) : metric.type === "roulette_play_count_chart" ? (
          <div className="metric-chart">
            <RoulettePlayCountChart
              data={data}
              interval={getDateRangeLabel()}
            />
          </div>
        ) : metric.type === "roulette_unique_players_chart" ? (
          <div className="metric-chart">
            <RouletteUniquePlayersChart
              data={data}
              interval={getDateRangeLabel()}
            />
          </div>
        ) : metric.type === "chart" ? (
          <div className="metric-chart">
            <div className="chart-placeholder">
              📊 Chart visualization
              <small>Connect to Recharts or Chart.js</small>
            </div>
          </div>
        ) : metric.type === "traffic_overview_widget" ? (
          <div className="metric-widget">
            <TrafficOverviewWidget
              interval={getDateRangeLabel()}
              apiBaseUrl={getCurrentCategoryConfig()?.apiBaseUrl || API_BASE_URLS.analytics}
            />
          </div>
        ) : metric.type === "web_traffic_trend_chart" ? (
          <div className="metric-chart">
            <WebsiteTrafficTrendChart
              data={data}
              interval={getDateRangeLabel()}
              graphName={metric.graphName || "Trends"}
              dataTitle={metric.dataTitle || "Count"}
            />
          </div>
        ) : metric.type === "traffic_by_device_chart" ? (
          <div className="metric-chart">
            <DeviceTrafficChart
              data={data}
              interval={getDateRangeLabel()}
            />
          </div>
        ) : metric.type === "traffic_by_preferred_lang_list" ? (
          <div className="metric-chart">
            <TrafficByLanguageList
              data={data}
              interval={getDateRangeLabel()}
            />
          </div>
        ) : metric.type === "chat_safety_overview_widget" ? (
          <div className="metric-widget">
            <ChatSafetyOverview
              interval={getDateRangeLabel()}
              apiBaseUrl={getCurrentCategoryConfig()?.apiBaseUrl || API_BASE_URLS.chatAnalytics}
            />
          </div>
        ) : metric.type === "alert_trend_chart" ? (
          <div className="metric-chart">
            <AlertTrendChart
              interval={getDateRangeLabel()}
              apiBaseUrl={getCurrentCategoryConfig()?.apiBaseUrl || API_BASE_URLS.chatAnalytics}
            />
          </div>
        ) : metric.type === "category_breakdown_chart" ? (
          <div className="metric-chart">
            <CategoryBreakdownChart
              interval={getDateRangeLabel()}
              apiBaseUrl={getCurrentCategoryConfig()?.apiBaseUrl || API_BASE_URLS.chatAnalytics}
            />
          </div>
        ) : metric.type === "severity_distribution_chart" ? (
          <div className="metric-chart">
            <SeverityDistributionChart
              interval={getDateRangeLabel()}
              apiBaseUrl={getCurrentCategoryConfig()?.apiBaseUrl || API_BASE_URLS.chatAnalytics}
            />
          </div>
        ) : metric.type === "repeat_offenders_list" ? (
          <div className="metric-list">
            <RepeatOffendersList
              interval={getDateRangeLabel()}
              apiBaseUrl={getCurrentCategoryConfig()?.apiBaseUrl || API_BASE_URLS.chatAnalytics}
            />
          </div>
        ) : metric.type === "peak_activity_heatmap" ? (
          <div className="metric-list">
            <PeakActivityHeatmap
              interval={getDateRangeLabel()}
              apiBaseUrl={getCurrentCategoryConfig()?.apiBaseUrl || API_BASE_URLS.chatAnalytics}
            />
          </div>
        ) : metric.type === "message_trend_chart" ? (
          <div className="metric-chart">
            <MessageTrendChart
              interval={getDateRangeLabel()}
              apiBaseUrl={getCurrentCategoryConfig()?.apiBaseUrl || API_BASE_URLS.chatAnalytics}
            />
          </div>
        ) : (
          <div className="metric-value">
            {formatValue(value, metric.type)}
          </div>
        )}
      </div>

      {data?.trend && (
        <div
          className={`metric-trend ${
            data.trend > 0 ? "positive" : "negative"
          }`}
        >
          <span className="trend-icon">{data.trend > 0 ? "↗" : "↘"}</span>
          <span className="trend-value">{Math.abs(data.trend)}%</span>
          <span className="trend-label">vs last period</span>
        </div>
      )}
    </div>
  );
};

  return (
    <div className="analytics-dashboard">
      <div className="dashboard-header">
        <div className="dashboard-title-section">
          <img
            src="https://conquerlifepublicbucket.s3.us-east-1.amazonaws.com/conquerLifeLogo.png"
            alt="Conquer Life Logo"
            className="dashboard-logo"
          />
          <h1>Conquer Life Analytics</h1>
           <button
          className="mobile-menu-toggle-icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle category menu"
        >
          <span className="menu-icon">☰</span>
        </button>
        </div>
        <div className="header-controls">
          <div className="date-range-container">
            <select
              className="date-range-selector"
              value={
                dateRange.startsWith("custom-") ||
                dateRange.startsWith("year-") ||
                dateRange.startsWith("month-")
                  ? dateRange.startsWith("custom-")
                    ? "custom"
                    : dateRange.startsWith("year-")
                    ? "year"
                    : "month"
                  : dateRange
              }
              onChange={handleDateRangeChange}
            >
              <optgroup label="Real-time">
                <option value="30min">Last 30 Minutes</option>
                <option value="24h">Last 24 Hours</option>
              </optgroup>
              <optgroup label="Days">
                <option value="7">Last 7 Days</option>
                <option value="10">Last 10 Days</option>
                <option value="30">Last 30 Days</option>
                <option value="90">Last 90 Days</option>
                <option value="custom">Custom Days...</option>
              </optgroup>
              <optgroup label="Months">
                <option value="1_month">Last Month</option>
                <option value="3_months">Last 3 Months</option>
                <option value="6_months">Last 6 Months</option>
                <option value="8_months">Last 8 Months</option>
                <option value="month">Custom Months...</option>
              </optgroup>
              <optgroup label="Year">
                <option value="past_year">Past Year</option>
                <option value="ytd">Year to Date</option>
                {/* <option value="year">Specific Year...</option> */}
              </optgroup>
            </select>

            {showCustomInput && (
              <>
                {/* <div
                  className="custom-input-overlay"
                  onClick={() => setShowCustomInput(false)}
                /> */}
                <div className="custom-input-container">
                  <input
                    type="number"
                    className="custom-input"
                    placeholder="Enter days"
                    min="1"
                    max="365"
                    value={customDays}
                    onChange={(e) => setCustomDays(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && handleCustomDaysSubmit()
                    }
                  />
                  <button
                    className="apply-btn"
                    onClick={handleCustomDaysSubmit}
                    disabled={!customDays || parseInt(customDays) <= 0}
                  >
                    Apply
                  </button>
                </div>
              </>
            )}

            {showMonthInput && (
              <>
                {/* <div
                  className="custom-input-overlay"
                  onClick={() => setShowMonthInput(false)}
                /> */}
                <div className="custom-input-container">
                  <input
                    type="number"
                    className="custom-input"
                    placeholder="Enter months no."
                    min="1"
                    max="60"
                    value={customMonth}
                    onChange={(e) => setCustomMonth(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && handleCustomMonthSubmit()
                    }
                  />
                  <button
                    className="apply-btn"
                    onClick={handleCustomMonthSubmit}
                    disabled={!customMonth || parseInt(customMonth) <= 0}
                  >
                    Apply
                  </button>
                </div>
              </>
            )}

            {/* {showYearInput && (
              <>
                <div
                  className="custom-input-overlay"
                  onClick={() => setShowYearInput(false)}
                />
                <div className="custom-input-container">
                  <input
                    type="number"
                    className="custom-input"
                    placeholder="Enter year"
                    min="2020"
                    max={new Date().getFullYear()}
                    value={customYear}
                    onChange={(e) => setCustomYear(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && handleCustomYearSubmit()
                    }
                  />
                  <button
                    className="apply-btn"
                    onClick={handleCustomYearSubmit}
                    disabled={
                      !customYear ||
                      parseInt(customYear) < 2020 ||
                      parseInt(customYear) > new Date().getFullYear()
                    }
                  >
                    Apply
                  </button>
                </div>
              </>
            )} */}
          </div>

          <button className="refresh-btn" onClick={loadCategoryData}>
            <svg
              className="refresh-icon"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
            </svg>
            <span className="refresh-text">Refresh</span>
          </button>
          <button
            className="logout-btn"
            onClick={() => {
              navigate("/login");
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span className="refresh-text">Logout</span>
          </button>
        </div>
      </div>

      <div className="dashboard-content">

        {isMobileMenuOpen && (
          <div
            className="mobile-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        <div className="main-content">
          <div className="content-header">
            <div className="content-header-left">
              <h2>
                {getCurrentCategoryIcon()}{" "}
                {getCurrentCategoryName()}{" "}
                Analytics
              </h2>
              <p className="content-subtitle">
                Overview of {getCurrentCategoryName().toLowerCase()} metrics and performance
              </p>
            </div>
            {/* Platform Selector - Only show for User Insights when User Trends is expanded */}
            {/* {selectedCategory === "user_insights" && expandedUserSections.user_trends && (
              <div className="content-header-right">
                <select
                  className="date-range-selector"
                  value={selectedPlatform}
                  onChange={(e) => setSelectedPlatform(e.target.value)}
                >
                  <option value="ALL_PLATFORMS">All Platforms</option>
                  <option value="IOS">iOS</option>
                  <option value="ANDROID">Android</option>
                  <option value="WEB">Web</option>
                </select>
              </div>
            )} */}
            {/* <div className="content-header-right">
              <select
                className="date-range-selector"
                value={deviceType}
                onChange={(e) => setDeviceType(e.target.value)}
              >
                <option value="all">All Devices</option>
                <option value="desktop">Desktop</option>
                <option value="ios">iOS</option>
                <option value="android">Android</option>
              </select>
            </div> */}
          </div>

            {selectedCategory === "ai_usage" ? (
              <>
                <div className="ai-section-wrapper">
                  <div className="ai-section">
                    <div
                      className="ai-section-header"
                      onClick={() => toggleSection("ai", "user_service")}
                    >
                      {(() => {
                        const key = "user_service";
                        const SectionIcon = sectionConfig[key].icon;

                        return (
                          <>
                            <div className="section-left">
                              <SectionIcon className="section-icon" />
                              <h3>{sectionConfig[key].label}</h3>
                            </div>
                          </>
                        );
                      })()}
                      <span>{expandedAISections.user_service ? "▼" : "▶"}</span>
                    </div>

                    {expandedAISections.user_service && (
                      <div className="ai-metrics-grid">
                        {metricsConfig.ai_usage.user_service.map(metric =>
                          renderMetricTile(metric)
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="ai-section-wrapper">
                  <div className="ai-section">
                    <div
                      className="ai-section-header"
                      onClick={() => toggleSection("ai", "chat_service")}
                    >
                      {(() => {
                        const key = "chat_service";
                        const SectionIcon = sectionConfig[key].icon;

                        return (
                          <>
                            <div className="section-left">
                              <SectionIcon className="section-icon" />
                              <h3>{sectionConfig[key].label}</h3>
                            </div>
                          </>
                        );
                      })()}
                      <span>{expandedAISections.chat_service ? "▼" : "▶"}</span>
                    </div>

                    {expandedAISections.chat_service && (
                      <div className="ai-metrics-grid">
                        {metricsConfig.ai_usage.chat_service.map(metric =>
                          renderMetricTile(metric)
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : selectedCategory === "user_insights" ? (
              <>
                {/* USER TRENDS */}
                <div className="ai-section-wrapper">
                  <div className="ai-section">
                    <div
                      className="ai-section-header"
                      onClick={() => toggleSection("user", "user_trends")}
                    >
                      {(() => {
                        const key = "user_trends";
                        const SectionIcon = sectionConfig[key].icon;

                        return (
                          <>
                            <div className="section-left">
                              <SectionIcon className="section-icon" />
                              <h3>{sectionConfig[key].label}</h3>
                            </div>
                          </>
                        );
                      })()}
                      <span>
                        {expandedUserSections.user_trends ? "▼" : "▶"}
                      </span>
                    </div>

                    {expandedUserSections.user_trends && (
                      <div className="ai-metrics-grid">
                        {metricsConfig.user_insights.user_trends.map(metric =>
                          renderMetricTile(metric)
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* USER INTERACTIONS */}
                <div className="ai-section-wrapper">
                  <div className="ai-section">
                    <div
                      className="ai-section-header"
                      onClick={() => toggleSection("user", "user_interactions")}
                    >
                      {(() => {
                        const key = "user_interactions";
                        const SectionIcon = sectionConfig[key].icon;

                        return (
                          <>
                            <div className="section-left">
                              <SectionIcon className="section-icon" />
                              <h3>{sectionConfig[key].label}</h3>
                            </div>
                          </>
                        );
                      })()}

                      <span>
                        {expandedUserSections.user_interactions ? "▼" : "▶"}
                      </span>
                    </div>

                    {expandedUserSections.user_interactions && (
                      <div className="ai-metrics-grid">
                        {metricsConfig.user_insights.pages.map(metric =>
                          renderMetricTile(metric)
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : selectedCategory === "chat_insights" ? (
              <>
                <div className="ai-section-wrapper">
                  <div className="ai-section">
                    <div
                      className="ai-section-header"
                      onClick={() => toggleSection("chat", "chat_trends")}
                    >
                      {(() => {
                        const key = "chat_trends";
                        const SectionIcon = sectionConfig[key].icon;

                        return (
                          <>
                            <div className="section-left">
                              <SectionIcon className="section-icon" />
                              <h3>{sectionConfig[key].label}</h3>
                            </div>
                          </>
                        );
                      })()}
                      <span>
                        {expandedChatSections.chat_trends ? "▼" : "▶"}
                      </span>
                    </div>

                    {expandedChatSections.chat_trends && (
                      <div className="ai-metrics-grid">
                        {metricsConfig.chat_insights.chat_trends.map(metric =>
                          renderMetricTile(metric)
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="ai-section-wrapper">
                  <div className="ai-section">
                    <div
                      className="ai-section-header"
                      onClick={() => toggleSection("chat", "chat_safety")}
                    >
                      {(() => {
                        const key = "chat_safety";
                        const SectionIcon = sectionConfig[key].icon;

                        return (
                          <>
                            <div className="section-left">
                              <SectionIcon className="section-icon" />
                              <h3>{sectionConfig[key].label}</h3>
                            </div>
                          </>
                        );
                      })()}

                      <span>
                        {expandedChatSections.chat_safety ? "▼" : "▶"}
                      </span>
                    </div>

                    {expandedChatSections.chat_safety && (
                      <div className="ai-metrics-grid">
                        {metricsConfig.chat_insights.chat_safety.map(metric =>
                          renderMetricTile(metric)
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="metrics-grid">
                {metricsConfig[selectedCategory]?.map(metric =>
                  renderMetricTile(metric)
                )}
              </div>
            )}
        </div>

        <div className={`sidebar ${isMobileMenuOpen ? "mobile-open" : ""}`}>
          <div className="sidebar-header">
            <h2 className="sidebar-title">Categories</h2>
            <button
              className="mobile-close-btn"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              ✕
            </button>
          </div>
          <nav className="category-nav">
            {categories.map((category) => (
              <div key={category.id}>
                {/* Parent or Standalone Category */}
                <button
                  className={`category-btn ${
                    category.type === "standalone" && selectedCategory === category.id
                      ? "active"
                      : category.type === "parent" && category.children?.some(c => c.id === selectedCategory)
                      ? "active"
                      : ""
                  }`}
                  onClick={() => handleCategoryChange(category.id, category.type === "parent")}
                  data-type={category.type}
                  style={{
                    "--category-color": category.color,
                  }}
                >
                  <span className="category-icon">
                    {isImageUrl(category.icon) ? (
                      <img 
                        src={category.icon} 
                        alt={category.name}
                        style={{ width: '30px', height: '30px', objectFit: 'contain' }}
                      />
                    ) : isReactIconComponent(category.icon) ? (
                      <category.icon size={30} />
                    ) : (
                      category.icon
                    )}
                  </span>
                  <span className="category-name">{category.name}</span>
                  {category.type === "parent" && (
                    <span className="expand-arrow">
                      {expandedCategories[category.id] ? "▼" : "▶"}
                    </span>
                  )}
                  {category.type === "standalone" && selectedCategory === category.id && (
                    <span className="active-indicator"></span>
                  )}
                </button>

                {/* Child Categories */}
                {category.type === "parent" && expandedCategories[category.id] && category.children && (
                  <div className="child-categories">
                    {category.children.map((child) => (
                      <button
                        key={child.id}
                        className={`category-btn child-btn ${
                          selectedCategory === child.id ? "active" : ""
                        }`}
                        onClick={() => handleCategoryChange(child.id, false)}
                        data-type="child"
                        style={{
                          "--category-color": child.color,
                        }}
                      >
                        <span className="category-icon">
                          {isImageUrl(child.icon) ? (
                            <img 
                              src={child.icon} 
                              alt={child.name}
                              style={{ width: '25px', height: '25px', objectFit: 'contain' }}
                            />
                          ) : isReactIconComponent(child.icon) ? (
                            <child.icon size={25} />
                          ) : (
                            child.icon
                          )}
                        </span>
                        <span className="category-name">{child.name}</span>
                        {selectedCategory === child.id && (
                          <span className="active-indicator"></span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>
      {expandedMetric && (
        <MetricModal
          isOpen={!!expandedMetric}
          onClose={closeModal}
          title={expandedMetric.title}
          icon={expandedMetric.icon}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          showCustomInput={showCustomInput}
          showMonthInput={showMonthInput}
          showYearInput={showYearInput}
          customDays={customDays}
          customMonth={customMonth}
          customYear={customYear}
          onCustomDaysChange={(e) => setCustomDays(e.target.value)}
          onCustomMonthChange={(e) => setCustomMonth(e.target.value)}
          onCustomYearChange={(e) => setCustomYear(e.target.value)}
          onCustomDaysSubmit={handleCustomDaysSubmit}
          onCustomMonthSubmit={handleCustomMonthSubmit}
          onCustomYearSubmit={handleCustomYearSubmit}
          onCloseCustomInput={() => {
            setShowCustomInput(false);
            setShowMonthInput(false);
            setShowYearInput(false);
          }}
          selectedPlatform={selectedPlatform}
          onPlatformChange={(e) => setSelectedPlatform(e.target.value)}
          showPlatformSelector={
            expandedMetric?.id === "daily_active_users" ||
            expandedMetric?.id === "user_growth" ||
            expandedMetric?.id === "top_8_popular_pages"
          }
        >
          {renderExpandedContent(expandedMetric)}
        </MetricModal>
      )}
    </div>
  );
};

export default AnalyticsDashboard;